import { useMemo, useState } from 'react';

import { OpenAIFunctionDefinition, OpenAIFunctionParameterProperty } from 'features/master/api/types';
import { ChevronDown, Search } from 'lucide-react';
import { Collapsible } from 'radix-ui';

import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const formatParameterTypes = (fn: OpenAIFunctionDefinition): string | null => {
  const { parameters } = fn.function;
  if (!parameters?.properties) return null;

  return Object.keys(parameters.properties)
    .map((key) => {
      const property = parameters.properties[key];
      const typeHint = property.enum?.join(',') ?? property.items?.enum?.join(',') ?? property.type ?? 'unknown';
      return `${key}[${typeHint}]`;
    })
    .join(', ');
};

const formatPropertyType = (property: OpenAIFunctionParameterProperty): string =>
  property.enum?.join(', ') ?? property.items?.enum?.join(', ') ?? property.type ?? '';

const functionMatchesSearch = (fn: OpenAIFunctionDefinition, searchTerm: string): boolean => {
  const normalized = searchTerm.trim().toLowerCase();
  if (!normalized) return true;

  const { name, description, parameters } = fn.function;
  if (name.toLowerCase().includes(normalized)) return true;
  if (description?.toLowerCase().includes(normalized)) return true;

  const properties = parameters?.properties ?? {};
  return Object.entries(properties).some(
    ([key, property]) =>
      key.toLowerCase().includes(normalized) ||
      property.description?.toLowerCase().includes(normalized) ||
      formatPropertyType(property).toLowerCase().includes(normalized)
  );
};

interface FunctionCollapsibleProps {
  fn: OpenAIFunctionDefinition;
}

const FunctionCollapsible = ({ fn }: FunctionCollapsibleProps) => {
  const { name, description, parameters } = fn.function;
  const parameterTypes = formatParameterTypes(fn);
  const properties = parameters?.properties ?? {};
  const propertyEntries = Object.entries(properties);

  return (
    <Collapsible.Root className="group/function min-w-0 overflow-hidden rounded-md border border-border-subtle bg-bg-surface">
      <Collapsible.Trigger asChild>
        <button
          type="button"
          className="flex w-full min-w-0 items-start gap-2 px-3 py-3 text-left"
          aria-label={`Expandir o contraer ${name}`}
        >
          <ChevronDown
            className={cn(
              'mt-0.5 size-4 shrink-0 text-text-muted transition-transform',
              'group-data-[state=open]/function:rotate-180'
            )}
          />
          <div className="min-w-0 flex-1">
            <p className="break-words text-sm font-semibold text-text-default">{name}</p>
            {parameterTypes && (
              <p className="mt-0.5 truncate font-mono text-[11px] text-text-muted">({parameterTypes})</p>
            )}
          </div>
        </button>
      </Collapsible.Trigger>
      <Collapsible.Content className="min-w-0 overflow-hidden">
        <div className="min-w-0 space-y-3 overflow-hidden border-t border-border-subtle px-3 pb-3 pt-2">
          <p className="break-words [overflow-wrap:anywhere] whitespace-pre-wrap text-sm text-text-muted">
            {description}
          </p>
          {propertyEntries.length > 0 && (
            <ul className="space-y-2 border-t border-border-subtle pt-3">
              {propertyEntries.map(([key, property]) => (
                <li key={key} className="min-w-0 text-xs">
                  <span className="break-all font-mono font-medium text-text-default">{key}</span>
                  <span className="break-words text-text-muted"> ({formatPropertyType(property)})</span>
                  {property.description && (
                    <p className="mt-0.5 break-words [overflow-wrap:anywhere] whitespace-pre-wrap text-text-muted">
                      {property.description}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
};

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
}

const SearchInput = ({ value, onChange }: SearchInputProps) => (
  <div className="relative">
    <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-text-muted" />
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Buscar funciones"
      className="h-8 pl-7 text-xs"
    />
  </div>
);

interface Props {
  functions: OpenAIFunctionDefinition[];
  isLoading?: boolean;
}

const PromptFunctionsPanel = ({ functions, isLoading = false }: Props) => {
  const [search, setSearch] = useState('');

  const filteredFunctions = useMemo(
    () => functions.filter((fn) => functionMatchesSearch(fn, search)),
    [functions, search]
  );

  const hasSearch = search.trim().length > 0;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
      <div className="shrink-0 space-y-3 border-b border-border-subtle px-4 pb-3 pt-2">
        <div>
          <p className="text-base font-semibold text-text-default">Funciones adicionales al prompt</p>
          <p className="text-xs text-text-muted">Herramientas disponibles para el asistente en este proyecto.</p>
        </div>
        {!isLoading && functions.length > 0 && <SearchInput value={search} onChange={setSearch} />}
      </div>

      <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-4 py-3">
        {isLoading && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        )}

        {!isLoading && functions.length === 0 && (
          <p className="rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-xs text-text-muted">
            No hay funciones adicionales configuradas para este proyecto.
          </p>
        )}

        {!isLoading && functions.length > 0 && (
          <>
            <div className="mb-3 flex items-center gap-2">
              <Badge variant="secondary" className="text-[10px]">
                {filteredFunctions.length}
              </Badge>
              <span className="text-xs text-text-muted">
                {hasSearch ? 'funciones encontradas' : 'funciones activas'}
              </span>
            </div>

            {filteredFunctions.length === 0 ? (
              <p className="rounded-md border border-border-subtle bg-bg-surface px-3 py-2 text-xs text-text-muted">
                No se encontraron funciones que coincidan con la búsqueda.
              </p>
            ) : (
              <div className="flex min-w-0 flex-col gap-2">
                {filteredFunctions.map((fn) => (
                  <FunctionCollapsible key={fn.function.name} fn={fn} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PromptFunctionsPanel;
